// oxlint-disable no-explicit-any

import { getIdWithPrefix } from '../../../../../../utils/id';
import { BIOMETRIC_EVENT_PREFIX } from './types';

export type Request<typeT extends string, dataT = unknown> = {
  type: typeT;
  requestId: string;
  data?: dataT;
};

export type Response<typeT extends string, dataT = unknown> = {
  type: typeT;
  requestId: string;
  success: boolean;
  data?: dataT;
  error?: string;
};

/**
 * ENVIRONMENT DETECTION
 * ====================
 * This function determines if we're running inside a React Native WebView.
 *
 * The React Native WebView component automatically injects:
 * - window.ReactNativeWebView object
 * - window.ReactNativeWebView.postMessage() function
 *
 * This allows us to differentiate between:
 * - Running in matrix-native app (WebView context) → use native biometric operations
 * - Running in regular web browser → fallback to web-based implementations
 */
export const isReactNativeWebView = () => {
  return (
    typeof window !== 'undefined' &&
    typeof (window as any).ReactNativeWebView !== 'undefined' &&
    typeof (window as any).ReactNativeWebView.postMessage === 'function'
  );
};

export const getResponseType = (type: string) => {
  return `${type}_response`;
};

/**
 * BIDIRECTIONAL WEBVIEW ↔ NATIVE COMMUNICATION
 * ============================================
 * This is the core function that enables communication between WebView and React Native.
 *
 * COMMUNICATION FLOW:
 * 1. WebView sends message via window.ReactNativeWebView.postMessage()
 * 2. Native receives message in Login.tsx onMessage handler
 * 3. Native routes biometric_* messages to useWebviewBiometric.ts
 * 4. Native performs actual biometric operation using biometric.native.ts
 * 5. Native sends response back via webViewRef.current.injectJavaScript()
 * 6. WebView receives response via window.addEventListener('message')
 * 7. Promise resolves with response data or rejects with error
 *
 * TIMEOUT HANDLING:
 * - 60-second timeout prevents hanging operations
 * - Essential for biometric operations which can be cancelled by user
 *
 * ERROR HANDLING:
 * - Network failures, user cancellation, hardware unavailability
 * - Always cleanup listeners and timeouts to prevent memory leaks
 */
export const sendMessageToReactNativeSDK = <type extends string, dataT = unknown>(
  type: type,
  data?: unknown,
): Promise<dataT> => {
  return new Promise((resolve, reject) => {
    if (!isReactNativeWebView()) {
      reject(new Error('React Native WebView not available'));
      return;
    }

    const requestId = getIdWithPrefix(BIOMETRIC_EVENT_PREFIX);
    const timeoutMs = 60000; // 60 second timeout - biometric operations can be slow

    // Set up response listener - waits for native to send response back
    const messageHandler = (event: CustomEvent) => {
      try {
        const response = event.detail as Response<type, dataT>;

        // Only handle responses that match our requestId
        if (response.type === getResponseType(type) && response.requestId === requestId) {
          // Clean up listeners and timeout
          window.removeEventListener('biometricResponse', messageHandler);
          clearTimeout(timeout);
          if (response.success) {
            resolve(response.data as dataT);
          } else {
            reject(new Error(response.error || 'Biometric operation failed'));
          }
        }
      } catch {
        // Ignore non-JSON messages or other message types
        // WebView can receive various messages, we only care about biometric responses
      }
    };

    // Set up timeout - prevent infinite waiting if native doesn't respond
    const timeout = setTimeout(() => {
      window.removeEventListener('biometricResponse', messageHandler);
      reject(new Error('Biometric operation timed out'));
    }, timeoutMs);

    // Register listener BEFORE sending message to avoid race conditions
    window.addEventListener('biometricResponse', messageHandler);

    try {
      // Send message to React Native native layer
      const message: Request<type, unknown> = { type, requestId, data };
      (window as any).ReactNativeWebView.postMessage(JSON.stringify(message));
    } catch (error) {
      clearTimeout(timeout);
      window.removeEventListener('biometricResponse', messageHandler);
      reject(error);
    }
  });
};
